// src/pages/admin/inhouse/ProductStock.jsx
import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Table, Badge, Card, Button, Pagination, Alert } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiArrowLeft, FiPackage, FiShoppingCart, FiAlertCircle, FiRefreshCw } from "react-icons/fi";

export default function InHouseProductStock() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [stockLogs, setStockLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0
    });
    const [currentPage, setCurrentPage] = useState(1);

    const fetchProductAndLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            // Fetch product details
            const productRes = await axios.get(`${process.env.REACT_APP_API_URL}/inhouse/products/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProduct(productRes.data.data);

            // Fetch stock logs
            const logsRes = await axios.get(`${process.env.REACT_APP_API_URL}/inhouse/products/${id}/stock-logs`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page }
            });

            const logsData = logsRes.data.data || [];
            setStockLogs(logsData.data || logsData);
            setPagination({
                current_page: logsData.current_page || 1,
                last_page: logsData.last_page || 1,
                per_page: logsData.per_page || 20,
                total: logsData.total || 0,
                from: logsData.from || 0,
                to: logsData.to || 0
            });
        } catch (err) {
            console.error("Error fetching stock data:", err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchProductAndLogs(currentPage);
    }, [fetchProductAndLogs, currentPage]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setCurrentPage(page);
        }
    };

    const getStockBadgeClass = (stock, minAlert) => {
        if (stock <= 0) return "bg-danger";
        if (stock <= minAlert) return "bg-warning";
        return "bg-success";
    };

    const getTypeBadge = (type) => {
        switch (type?.toLowerCase()) {
            case 'purchase':
                return <Badge bg="success">{t("Purchase")}</Badge>;
            case 'sale':
                return <Badge bg="info">{t("Sale")}</Badge>;
            case 'adjustment':
                return <Badge bg="warning">{t("Adjustment")}</Badge>;
            default:
                return <Badge bg="secondary">{type || t("Unknown")}</Badge>;
        }
    };

    const getQuantityDisplay = (qty, type) => {
        const isPositive = qty > 0;
        const color = isPositive ? "text-success" : "text-danger";
        const sign = isPositive ? "+" : "";
        return <span className={color}>{sign}{qty}</span>;
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </div>
                    <p className="mt-3">{t("Loading stock data...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (!product) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <Alert variant="danger">
                        <Alert.Heading>{t("Product Not Found")}</Alert.Heading>
                        <p>{t("The requested product does not exist.")}</p>
                    </Alert>
                </div>
            </PageLayout>
        );
    }

    const isLowStock = product.stock_qty <= product.minimum_alert_qty;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('Stock Management')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="/inhouse/products" className="mc-breadcrumb-link">{t('Products')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('Stock History')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <Card className="mb-4">
                        <Card.Body>
                            <ButtonComponent
                                className="btn btn-secondary mb-4"
                                onClick={() => navigate("/inhouse/products")}
                                text={<><FiArrowLeft className="me-1" /> {t("Back to Products")}</>}
                            />

                            {/* Product Info Card */}
                            <div className="bg-light p-4 rounded mb-4">
                                <Row>
                                    <Col md={8}>
                                        <h4>{product.name}</h4>
                                        <div className="text-muted mb-2">
                                            {product.purchase_unit} ({product.unit_quantity}{product.unit_type}/{product.purchase_unit})
                                        </div>
                                        <p className="mb-2">{product.description}</p>
                                    </Col>
                                    <Col md={4}>
                                        <div className="text-md-end">
                                            <div className="mb-3">
                                                <div className="text-muted small">{t("Current Stock")}</div>
                                                <h2 className={`mb-0 ${isLowStock ? 'text-warning' : ''}`}>
                                                    {product.stock_qty} {product.unit_type}
                                                    {isLowStock && <FiAlertCircle className="ms-2 text-warning" size={24} />}
                                                </h2>
                                                {product.minimum_alert_qty > 0 && (
                                                    <small className="text-muted">
                                                        {t("Min Alert")}: {product.minimum_alert_qty} {product.unit_type}
                                                    </small>
                                                )}
                                            </div>
                                            <div className="mb-2">
                                                <Badge className={getStockBadgeClass(product.stock_qty, product.minimum_alert_qty)}>
                                                    {isLowStock ? t("Low Stock") : t("In Stock")}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mt-3 pt-3 border-top">
                                    <Col md={4}>
                                        <div className="text-center">
                                            <div className="text-muted small">{t("Current Cost")}</div>
                                            <h5 className="mb-0">{parseFloat(product.current_cost || 0).toFixed(2)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="text-center">
                                            <div className="text-muted small">{t("Sale Price")}</div>
                                            <h5 className="mb-0">{parseFloat(product.sale_price || 0).toFixed(2)}</h5>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="text-center">
                                            <div className="text-muted small">{t("Purchase Unit")}</div>
                                            <h5 className="mb-0">{product.purchase_unit}</h5>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Stock History */}
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h5 className="mb-0">
                                    <FiPackage className="me-2" />
                                    {t("Stock Movement History")}
                                </h5>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => fetchProductAndLogs(currentPage)}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <FiRefreshCw size={14} /> {t("Refresh")}
                                </Button>
                            </div>

                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '15%' }}>{t('Date')}</th>
                                            <th style={{ width: '10%' }}>{t('Type')}</th>
                                            <th className="text-end" style={{ width: '15%' }}>{t('Quantity Change')}</th>
                                            <th className="text-end" style={{ width: '15%' }}>{t('Remaining Stock')}</th>
                                            <th style={{ width: '45%' }}>{t('Notes')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stockLogs.length > 0 ? (
                                            stockLogs.map((log, index) => (
                                                <tr key={log.id || index}>
                                                    <td>{new Date(log.created_at).toLocaleString()}</td>
                                                    <td>{getTypeBadge(log.type)}</td>
                                                    <td className="text-end">
                                                        {getQuantityDisplay(log.qty, log.type)}
                                                        <small className="text-muted ms-1">{product.unit_type}</small>
                                                    </td>
                                                    <td className="text-end">
                                                        {log.remaining_stock} <small className="text-muted">{product.unit_type}</small>
                                                    </td>
                                                    <td>
                                                        <small>{log.notes || '-'}</small>
                                                        {log.reference_type && (
                                                            <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                                                                Ref: {log.reference_type} #{log.reference_id}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <FiPackage size={48} className="text-muted mb-3" />
                                                    <p className="mb-0">{t("No stock movements found")}</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {pagination.last_page > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2 mt-3">
                                    <div className="text-muted small">
                                        {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("entries")}
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First
                                            onClick={() => handlePageChange(1)}
                                            disabled={pagination.current_page === 1}
                                        />
                                        <Pagination.Prev
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1}
                                        />

                                        {(() => {
                                            const pages = [];
                                            const maxPages = Math.min(5, pagination.last_page);
                                            let startPage = Math.max(1, pagination.current_page - 2);
                                            let endPage = Math.min(pagination.last_page, startPage + maxPages - 1);

                                            if (endPage - startPage + 1 < maxPages) {
                                                startPage = Math.max(1, endPage - maxPages + 1);
                                            }

                                            for (let i = startPage; i <= endPage; i++) {
                                                pages.push(
                                                    <Pagination.Item
                                                        key={i}
                                                        active={i === pagination.current_page}
                                                        onClick={() => handlePageChange(i)}
                                                    >
                                                        {i}
                                                    </Pagination.Item>
                                                );
                                            }
                                            return pages;
                                        })()}

                                        <Pagination.Next
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.last_page}
                                        />
                                        <Pagination.Last
                                            onClick={() => handlePageChange(pagination.last_page)}
                                            disabled={pagination.current_page === pagination.last_page}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}